import type { MailboxDto } from "@bafe/api-client";
import { DataTable, StatusPill, type Column } from "@bafe/ui";
import { formatBytes } from "@bafe/validation";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { formatDateTime, formatRelative } from "../lib/format";

function lastActiveLabel(mailbox: MailboxDto): ReactNode {
	if (!mailbox.lastActiveAt) return "No activity recorded";
	const relative = formatRelative(mailbox.lastActiveAt);
	const exact = formatDateTime(mailbox.lastActiveAt);
	const by = mailbox.lastActiveBy?.trim();
	const title = by ? `${exact} · ${by}` : exact;
	return <span title={title}>{relative}</span>;
}
import { mailboxStatusDescriptor } from "../lib/status";

const BASE_COLUMNS: Column<MailboxDto>[] = [
	{
		id: "address",
		header: "Mailbox",
		primary: true,
		render: (mailbox) => (
			<Link
				to={`/mail/mailboxes/${mailbox.id}`}
				className="font-medium text-(--text-primary) underline-offset-2 hover:underline"
			>
				{mailbox.emailAddress}
			</Link>
		),
	},
	{
		id: "name",
		header: "Name",
		render: (mailbox) => mailbox.displayName || "—",
	},
	{
		id: "domain",
		header: "Domain",
		render: (mailbox) => mailbox.domainName,
	},
	{
		id: "storage",
		header: "Storage",
		render: (mailbox) => (
			<span>
				{formatBytes(mailbox.usedBytes)}
				{mailbox.quotaBytes !== null && (
					<span className="text-(--text-muted)"> · limit {formatBytes(mailbox.quotaBytes)}</span>
				)}
			</span>
		),
	},
	{
		id: "status",
		header: "Status",
		render: (mailbox) => {
			const descriptor = mailboxStatusDescriptor(mailbox);
			return <StatusPill tone={descriptor.tone}>{descriptor.label}</StatusPill>;
		},
	},
	{
		id: "active",
		header: "Last active",
		render: (mailbox) => lastActiveLabel(mailbox),
	},
];

export interface MailboxTableProps {
	rows: MailboxDto[];
	empty: ReactNode;
	/** Domain is redundant when the table already lives on a domain page. */
	hideDomain?: boolean;
	caption: string;
}

export function MailboxTable({ rows, empty, hideDomain = false, caption }: MailboxTableProps) {
	const columns = hideDomain ? BASE_COLUMNS.filter((column) => column.id !== "domain") : BASE_COLUMNS;

	return (
		<DataTable
			caption={caption}
			columns={columns}
			rows={rows}
			rowKey={(mailbox) => mailbox.id}
			empty={empty}
		/>
	);
}
